import { CRAComplianceSubQuestion, RequirementLibraryEntry } from "../types";

interface CRAComplianceQuestionProps {
  index: number;
  question: {
    ref: string;
    displayNumber?: string;
    name: string;
    question: string;
    subQuestions: CRAComplianceSubQuestion[];
    answerOptions: string[];
    expectedEvidence: string;
    requirementLibraryIds?: number[];
    requirementLibrary?: RequirementLibraryEntry[];
  };
  answers: Record<string, { answer: string; details: string }>;
  onChange: (
    questionRef: string,
    field: "answer" | "details",
    value: string,
  ) => void;
  disabled?: boolean;
}

// Normalise long option labels for the table header
function shortLabel(opt: string) {
  if (opt === "Yes") return "Yes";
  if (opt === "No") return "No";
  if (opt.toLowerCase().startsWith("not applicable")) return "N/A";
  return opt.length > 10 ? opt.slice(0, 8) + "…" : opt;
}

// Colour for the selected radio
function selectedColor(opt: string) {
  if (opt === "Yes")
    return "bg-green-500 border-green-500 dark:bg-green-600 dark:border-green-600";
  if (opt === "No")
    return "bg-red-500 border-red-500 dark:bg-red-600 dark:border-red-600";
  return "bg-gray-500 border-gray-500 dark:bg-gray-500 dark:border-gray-500";
}

/** Always-visible evidence hint beneath each question row */
function EvidenceBadge({ text }: { text: string }) {
  if (!text) return null;
  return (
    <div className="mt-2 rounded-md bg-teal-50/80 dark:bg-teal-900/15 px-3 py-2">
      <p className="text-xs leading-relaxed text-teal-900/80 dark:text-teal-200/80">
        <span className="font-semibold text-teal-600 dark:text-teal-400">
          Evidence examples:
        </span>{" "}
        {text}
      </p>
    </div>
  );
}

export default function CRAComplianceQuestionCard({
  question,
  answers,
  onChange,
  disabled = false,
}: CRAComplianceQuestionProps) {
  const hasSubQuestions =
    question.subQuestions && question.subQuestions.length > 0;

  // Build the list of answerable rows
  const rows: {
    ref: string;
    displayRef: string;
    name: string;
    question: string;
    expectedEvidence: string;
    answerOptions: string[];
    requirementLibraryIds: number[];
    requirementLibrary: RequirementLibraryEntry[];
  }[] = hasSubQuestions
    ? question.subQuestions.map((sq) => ({
        ref: sq.ref,
        displayRef: sq.displayNumber || sq.ref,
        name: sq.name || "",
        question: sq.question,
        expectedEvidence: sq.expectedEvidence,
        answerOptions: sq.answerOptions,
        requirementLibraryIds: sq.requirementLibraryIds || [],
        requirementLibrary: sq.requirementLibrary || [],
      }))
    : [
        {
          ref: question.ref,
          displayRef: question.displayNumber || question.ref,
          name: question.name,
          question: question.question,
          expectedEvidence: question.expectedEvidence,
          answerOptions: question.answerOptions,
          requirementLibraryIds: question.requirementLibraryIds || [],
          requirementLibrary: question.requirementLibrary || [],
        },
      ];

  // Collect all unique answer options across rows for column headers
  const allOptions: string[] = [];
  rows.forEach((r) =>
    r.answerOptions.forEach((o) => {
      if (!allOptions.includes(o)) allOptions.push(o);
    }),
  );

  return (
    <div className="w-full">
      {/* ── Section header ── */}
      <div className="mb-3">
        <h4 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          {question.displayNumber || question.ref}. {question.name}
        </h4>
        {hasSubQuestions && (
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 leading-relaxed whitespace-pre-line">
            {question.question}
          </p>
        )}
        {/* Parent-level evidence examples (for questions with sub-questions) */}
        {hasSubQuestions && question.expectedEvidence && (
          <div className="mt-2 rounded-md bg-teal-50/80 dark:bg-teal-900/15 border border-teal-200/50 dark:border-teal-700/25 px-3 py-2">
            <p className="text-xs leading-relaxed text-teal-900/80 dark:text-teal-200/80">
              <span className="font-semibold text-teal-600 dark:text-teal-400">
                Evidence examples:
              </span>{" "}
              {question.expectedEvidence}
            </p>
          </div>
        )}
      </div>

      {/* ── Table ── */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        {/* Header */}
        <div
          className="grid items-center gap-0 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400"
          style={{
            gridTemplateColumns: `3rem 1fr ${allOptions.map(() => "5rem").join(" ")}`,
          }}
        >
          <div className="px-2 py-2.5 text-center">#</div>
          <div className="px-4 py-2.5 flex items-center gap-1.5">
            Requirement
            <span className="relative group cursor-help">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="invisible group-hover:visible absolute left-0 top-5 z-50 w-72 rounded-md bg-gray-900 dark:bg-gray-700 text-white text-[11px] font-normal normal-case tracking-normal leading-relaxed p-2.5 shadow-lg">
                Badges like{" "}
                <span className="font-semibold text-indigo-300">CRA #42</span>{" "}
                refer to the CRA Requirement Library ID. Use the{" "}
                <em>View full requirement text</em> toggle below each row to
                read the complete requirement description.
              </span>
            </span>
          </div>
          {allOptions.map((opt) => (
            <div key={opt} className="px-1 py-2.5 text-center">
              {shortLabel(opt)}
            </div>
          ))}
        </div>

        {/* Rows */}
        {rows.map((row) => {
          const ans = answers[row.ref] || { answer: "", details: "" };

          return (
            <div key={row.ref}>
              {/* Main row */}
              <div
                className={`grid items-center gap-0 border-b border-gray-100 dark:border-gray-800 transition-colors ${
                  ans.answer === "No"
                    ? "bg-red-50/40 dark:bg-red-900/5"
                    : ans.answer === "Yes"
                      ? "bg-green-50/30 dark:bg-green-900/5"
                      : "bg-white dark:bg-gray-900"
                }`}
                style={{
                  gridTemplateColumns: `3rem 1fr ${allOptions.map(() => "5rem").join(" ")}`,
                }}
              >
                {/* Ref number */}
                <div className="px-2 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 text-center">
                  {row.displayRef}
                </div>

                {/* Name / header + always-visible evidence examples */}
                <div className="px-4 py-3 min-w-0">
                  {hasSubQuestions ? (
                    <>
                      <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 leading-relaxed">
                        {row.name || row.question}
                      </span>
                      {row.name && (
                        <p
                          className="text-sm text-gray-600 dark:text-gray-300 mt-1 leading-relaxed whitespace-pre-line"
                          title={row.question}
                        >
                          {row.question}
                        </p>
                      )}
                    </>
                  ) : (
                    <span className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed whitespace-pre-line">
                      {row.question}
                    </span>
                  )}
                  {/* Requirement Library IDs */}
                  {row.requirementLibrary.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {row.requirementLibrary.map((req) => (
                        <span
                          key={req.id}
                          className="inline-block text-[10px] leading-tight px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 font-semibold"
                          title={`${req.title}${req.craReference ? ` (${req.craReference})` : ""}`}
                        >
                          CRA #{req.id}
                        </span>
                      ))}
                    </div>
                  )}
                  {/* Full requirement text — collapsible scalable box */}
                  {row.requirementLibrary.some((r) => r.description) && (
                    <details className="mt-2 group/req">
                      <summary className="cursor-pointer text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 select-none inline-flex items-center gap-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-3 w-3 transition-transform group-open/req:rotate-90"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        View full requirement text
                      </summary>
                      <div
                        className="mt-1.5 rounded-md border border-indigo-200 dark:border-indigo-800/50 bg-indigo-50/40 dark:bg-indigo-900/10 p-3 text-xs leading-relaxed text-gray-800 dark:text-gray-200 overflow-auto resize-y"
                        style={{ minHeight: "6rem", maxHeight: "24rem" }}
                      >
                        {row.requirementLibrary.map((req, idx) => (
                          <div
                            key={req.id}
                            className={
                              idx > 0
                                ? "mt-3 pt-3 border-t border-indigo-200/60 dark:border-indigo-800/40"
                                : ""
                            }
                          >
                            <div className="font-semibold text-indigo-700 dark:text-indigo-300">
                              CRA #{req.id} — {req.title}
                              {req.craReference && (
                                <span className="ml-1 font-normal text-indigo-500 dark:text-indigo-400">
                                  ({req.craReference})
                                </span>
                              )}
                            </div>
                            {req.description && (
                              <p className="mt-1 whitespace-pre-line">
                                {req.description}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                  {/* Evidence examples – always visible */}
                  <EvidenceBadge text={row.expectedEvidence} />
                </div>

                {/* Answer radio buttons */}
                {allOptions.map((opt) => {
                  const isAvailable = row.answerOptions.includes(opt);
                  const isSelected = ans.answer === opt;
                  return (
                    <div
                      key={opt}
                      className="flex items-center justify-center py-3"
                    >
                      {isAvailable ? (
                        <button
                          type="button"
                          disabled={disabled}
                          onClick={() => onChange(row.ref, "answer", opt)}
                          className={`w-6 h-6 rounded-full border-2 transition-all flex items-center justify-center ${
                            isSelected
                              ? `${selectedColor(opt)} text-white`
                              : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 bg-white dark:bg-gray-800"
                          } disabled:opacity-40 disabled:cursor-not-allowed`}
                          title={opt}
                        >
                          {isSelected && (
                            <div className="w-2 h-2 rounded-full bg-white" />
                          )}
                        </button>
                      ) : (
                        <span className="text-gray-300 dark:text-gray-700 text-xs">
                          —
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* N/A info box – shown when "Not applicable" is selected */}
              {ans.answer.toLowerCase().startsWith("not applicable") && (
                <p className="mx-4 my-2 text-xs leading-relaxed text-sky-700 dark:text-sky-300">
                  ⚠ You can only choose <strong>N/A</strong> if you have
                  documented the justification aligned with the risk assessment
                  in your issue/requirement management tool.
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
